// react and external-libs
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
// MUI
import { AccordionDetails, Typography, useTheme } from '@mui/material';
// interface
import { Company, Contact } from '../../../../interfaces/users.interface';
// service
import { usePrevious } from '../../../../services/stateHelpers';
// components
import SiteSidebar from './SiteSidebar';
import LocationForm from '../LocationForm';
import StandardDialog from '../../../Modals/StandardDialog';
import { DataCell, DataRow } from '../../../Tables/DataList';
import { AccordionCollapse, AccordionCollapseSummary } from '../../../Layout/Collapsible';
import { useAppSelector } from '../../../../app/hooks';
import SiteRowWithWorkflow from './SiteRowWithWorkflow';
import { InfoOutlined } from '@mui/icons-material';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    nonCompliant: true;
    assessorApproved: true;
    complete: true;
  }
}

type SitesAccordionProps = {
  company: Company;
  companyCallback: any;
  userCanAdminCompany: boolean;
};

export default function SitesAccordion(props: SitesAccordionProps) {
  const theme = useTheme();
  const { company, companyCallback, userCanAdminCompany } = props;
  const { workflows: fullWf } = useAppSelector((state) => state.workflow);
  // external lib and redux
  const { profileSection } = useParams();
  const prevLocations = usePrevious(company.locations);
  // states
  const [expanded, setExpanded] = useState(false);
  const [createSiteOpen, setCreateSiteOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [canStartNewQuestionniare, setCanStartNewQuestionniare] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Contact | null>(null);
  // short/simple functions
  const handleCreateSiteOpen = useCallback(() => setCreateSiteOpen(true), []);
  const handleCreateSiteClose = useCallback(() => setCreateSiteOpen(false), []);
  const handleSetCanStartNewQuestionniare = useCallback(() => {
    setCanStartNewQuestionniare(true);
  }, []);

  const handleRowClick = useCallback((site: Contact) => {
    setSelectedSite(site);
    setIsSidebarOpen(true);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
    setSelectedSite(null);
  }, []);

  const handleSiteCreation = useCallback(
    (companyResponse: Company) => {
      companyCallback(companyResponse);
      handleCreateSiteClose();
    },
    [companyCallback],
  );

  const sortedSites = useMemo(
    () => [...company.locations].sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0)),
    [company.locations],
  );

  const handleSuccessfulSiteEdit = useCallback((companyResponse: Company, updateType: 'edit' | 'delete') => {
    companyCallback(companyResponse);
    const currentSite = companyResponse.locations.find((site) => site.id === selectedSite?.id);
    if (currentSite) {
      setSelectedSite(currentSite);
    }
    if (updateType === 'delete') {
      handleSidebarClose();
    }
  }, [companyCallback, handleSidebarClose, selectedSite?.id]);

  // if url params profile section is "sites", expand the accordion
  useEffect(() => {
    if (!profileSection) {
      setExpanded(false);
      return;
    }
    setExpanded(profileSection === 'sites');
  }, [profileSection]);

  useEffect(() => {
    if (!prevLocations || !company.locations || (prevLocations as Contact[]).length === company.locations.length)
      return;
    setExpanded(true);
  }, [company.locations]);

  useEffect(() => {
    if (!isSidebarOpen) {
      setCanStartNewQuestionniare(false);
    }
  }, [isSidebarOpen]);

  return (
    <>
      <AccordionCollapse
        expanded={expanded}
        onChange={() => {
          setExpanded(!expanded);
        }}
      >
        <AccordionCollapseSummary
          name='sites-header'
          title='Sites'
          count={company?.locations.length}
          addItem={() => handleCreateSiteOpen()}
          addItemLabel='Site'
          addItemTest={userCanAdminCompany}
        >
          <StandardDialog title='Add New Site' handleClose={handleCreateSiteClose} isOpen={createSiteOpen}>
            <LocationForm
              submitButtonText='Add Site'
              company={company}
              successCallback={handleSiteCreation}
              modalClose={() => handleCreateSiteClose()}
            />
          </StandardDialog>
        </AccordionCollapseSummary>
        <AccordionDetails>
          <Typography
            variant="body1"
            sx={{
              backgroundColor: '#F3F5FB',
              color: theme.palette.text.primary,
              mb: 1,
              ml: 1,
              fontSize: '0.95rem',
              padding: 2,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <InfoOutlined fontSize="inherit" />
            Click the site row to see more details in the sidebar
          </Typography>
          <DataRow header sx={{ flexWrap: 'nowrap' }}>
            <DataCell xs={0.75} minWidth={90} textAlign='center'>
              Shield
            </DataCell>
            <DataCell xs={2}>Name</DataCell>
            <DataCell xs={2}>Applications</DataCell>
            <DataCell xs={2}>Services</DataCell>
            <DataCell xs={3}>Status</DataCell>
            <DataCell xs={1.75} minWidth={200} textAlign='center'>
              Actions
            </DataCell>
            <DataCell xs={0.5}>&nbsp;</DataCell>
          </DataRow>
          {!!company.locations.length &&
            typeof company.locations[0] !== 'number' &&
            sortedSites.map((site: Contact) => (
              <SiteRowWithWorkflow
                key={site.id}
                company={company}
                site={site}
                fullWf={fullWf}
                userCanAdminCompany={userCanAdminCompany}
                companyCallback={companyCallback}
                onRowClick={handleRowClick}
                handleSetCanStartNewQuestionniare={handleSetCanStartNewQuestionniare}
              />
            ))}
        </AccordionDetails>
      </AccordionCollapse>
      <SiteSidebar
        canStartNewQuestionniare={canStartNewQuestionniare}
        isOpen={isSidebarOpen}
        company={company}
        site={selectedSite}
        onClose={handleSidebarClose}
        companyCallback={handleSuccessfulSiteEdit}
      />
    </>
  );
}
